import { useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Col, Empty, Input, Row } from 'antd'
import clsx from 'clsx'
import { parseAsBoolean, parseAsInteger, parseAsString, useQueryState } from 'nuqs'

import B2becPagination from '@/components/B2becPagination/B2becPagination'
import B2becSorting from '@/components/B2BecSorting'
import { ListViewIcon, SearchIcon, TileViewIcon } from '@/components/Icons'
import ProductCard from '@/components/ProductCard'
import B2becCardSkeleton from '@/components/Skeletons/B2becCardSkeleton'
import type { Machine } from '@/services/machine'
import useAdobeAnalysis from '@/hooks/useAdobeAnalysis'
import useBreadcrumbs from '@/hooks/useBreadcrumbs'
import useDocumentTitle from '@/hooks/useDocumentTitle'
import { PAGE_SIZE } from '@/libs/constants'
import { PAGE_TYPES } from '@/libs/constants/adobeAnalytics'
import { isValidArray } from '@/libs/utils/array'

import styles from './MyMachinePage.module.scss'
import ProductListView from './ProductCardTable'
import { useMyMachines } from './useMyMachines'

const machineSortingOptions = [
  {
    key: 'ByCategory',
    sortOrder: 1,
    text: 'sorting.sortBy.category',
  },
  {
    key: 'ByOrderDateAsc',
    sortOrder: 2,
    text: 'sorting.sortBy.dateAscending',
  },
  {
    key: 'ByOrderDateDesc',
    sortOrder: 3,
    text: 'sorting.sortBy.dateDescending',
  },
  {
    key: 'ByNameAsc',
    sortOrder: 4,
    text: 'sorting.sortBy.nameAscending',
  },
  {
    key: 'ByNameDesc',
    sortOrder: 5,
    text: 'sorting.sortBy.nameDescending',
  },
  {
    key: 'ByPriceAsc',
    sortOrder: 6,
    text: 'sorting.sortBy.priceAscending',
  },
  {
    key: 'ByPriceDesc',
    sortOrder: 7,
    text: 'sorting.sortBy.priceDescending',
  },
]

type MachineSortOption = (typeof machineSortingOptions)[number]

interface MyMachinesPageProps {
  breadcrumbs: {
    title: string
    path: string
  }[]
}

const MyMachinesPage = ({ breadcrumbs }: MyMachinesPageProps) => {
  useDocumentTitle('pageTitle.myMachines')
  useBreadcrumbs(breadcrumbs)

  useAdobeAnalysis(null, {
    pageName: 'my machine',
    pageId: '7',
    pageType: PAGE_TYPES.MACHINE,
  })

  const { t } = useTranslation()
  const elementRef = useRef([])

  const [showAsListView, setShowAsListView] = useQueryState(
    'list',
    parseAsBoolean.withDefault(false).withOptions({
      history: 'push',
    }),
  )
  const [sortOrder, setSortOrder] = useQueryState(
    's',
    parseAsString.withDefault(machineSortingOptions[0].key).withOptions({
      history: 'push',
    }),
  )
  const [currentPage, setCurrentPage] = useQueryState(
    'page',
    parseAsInteger.withDefault(1).withOptions({
      history: 'push',
    }),
  )
  const [searchText, setSearchText] = useQueryState(
    'q',
    parseAsString.withDefault('').withOptions({
      history: 'push',
    }),
  )

  const myMachines = useMyMachines({
    pageNumber: currentPage,
    pageSize: PAGE_SIZE[12],
    sortOrder,
    searchText,
  })

  const handleSort = (value: MachineSortOption) => {
    setSortOrder(value.key)
    setCurrentPage(1)
  }

  const handlePageChange = useCallback(
    (current: number) => {
      setCurrentPage(current)
    },
    [setCurrentPage],
  )

  const renderProductGridView = () => {
    return myMachines.isFetching
      ? (
        <B2becCardSkeleton
          columns={{ xs: 24, sm: 24, md: 12, lg: 8, xl: 8, xxl: 6 }}
          hasPrice={false}
          imageHeight={530}
          number={8}
          gutter={[16, 16]}
          style={{ marginTop: 30 }}
        />
      )
      : isValidArray(myMachines.data?.items)
      ? (
        <Row
          className="product-card__wrapper"
          gutter={[16, 16]}
          style={{ marginTop: 30 }}
        >
          {myMachines.data?.items.map((item, index) => (
            <Col
              key={`${item?.materialNumber}-${item?.salesOrderNumber}`}
              ref={(element) => {
                elementRef.current[index] = element
              }}
              className="card-item"
              id={`${item?.materialNumber}-${item?.salesOrderNumber}`}
              lg={8}
              md={12}
              sm={24}
              xl={8}
              xs={24}
              xxl={6}
            >
              <ProductCard
                ref={elementRef}
                category={item?.categoryName}
                date={item?.orderDate}
                img={item?.imgUrl}
                materialId={item?.materialNumber}
                name={item?.name}
                salesOrderNumber={item?.salesOrderNumber}
                serialNumber={item?.serialNumber}
              />
            </Col>
          ))}
        </Row>
      )
      : <Empty description={t('noData')} />
  }

  const renderContent = (array: Machine[]) => {
    if (showAsListView) {
      return <ProductListView dataSrc={array} isLoading={myMachines.isFetching} />
    }
    return renderProductGridView()
  }

  return (
    <div className="container">
      <div className={styles.wrapper}>
        <Row className="mt-4">
          <Col xl={12} lg={12} md={12} sm={12} xs={12}>
            <h1 className={styles.heading}>{t('myMachines.title')}</h1>
          </Col>
        </Row>
        <Row justify="space-between" align="bottom">
          <Col
            xl={8}
            lg={8}
            md={8}
            sm={8}
            xs={8}
            className="my-machines__sorting-wrapper text-right"
          >
            <Input
              value={searchText}
              onChange={(event) => {
                setSearchText(event.target.value)
                setCurrentPage(1)
              }}
              allowClear
              prefix={<SearchIcon />}
            />
          </Col>
          <Col>
            <B2becSorting
              onSort={handleSort}
              options={machineSortingOptions}
              sortOption={machineSortingOptions.find(option => option.key === sortOrder)}
            />
            <span className={styles.iconContainer}>
              <div
                className={clsx(styles.iconWrapper, {
                  [styles.active]: !showAsListView,
                })}
              >
                <TileViewIcon
                  onClick={async () => {
                    setShowAsListView(false)
                  }}
                />
              </div>
              <div
                className={clsx(styles.iconWrapper, {
                  [styles.active]: showAsListView,
                })}
              >
                <ListViewIcon
                  onClick={async () => {
                    setShowAsListView(true)
                  }}
                />
              </div>
            </span>
          </Col>
        </Row>

        {renderContent(myMachines.data?.items)}

        <B2becPagination
          current={currentPage}
          total={myMachines.data?.totalCount}
          onPageChange={handlePageChange}
        />
      </div>
    </div>
  )
}

export default MyMachinesPage
